<template>
  <a-drawer width="80%" :visible="open" :destroyOnClose="true" @close="onClose">
    <template slot="title">
      <span>{{ formTitle }}</span>
    </template>
    <a-spin :spinning="loading">
      <div class="user-detail-wrap">
        <div class="detail-section">
          <a-divider orientation="left" class="detail-divider">基础信息</a-divider>
          <a-descriptions :column="4" bordered size="small">
            <a-descriptions-item label="用户类型">
              <a-tag v-if="form.type === 0" color="blue">雇主端</a-tag>
              <a-tag v-else-if="form.type === 1" color="green">师傅端</a-tag>
              <span v-else>-</span>
            </a-descriptions-item>
            <a-descriptions-item label="手机号">{{ form.mobile || '-' }}</a-descriptions-item>
            <a-descriptions-item label="昵称">{{ form.nickname || '-' }}</a-descriptions-item>
            <a-descriptions-item v-if="hasText(form.contactPersonName)" label="联系人姓名">{{ form.contactPersonName }}</a-descriptions-item>
            <a-descriptions-item v-if="hasText(form.contactPersonPhone)" label="联系人手机号">{{ form.contactPersonPhone }}</a-descriptions-item>
            <a-descriptions-item v-if="form.type === 1 || (form.creditScore != null && form.creditScore !== '')" label="信用分">{{ form.creditScore != null ? form.creditScore : '-' }}</a-descriptions-item>
            <a-descriptions-item v-if="form.commentScore != null && form.commentScore !== ''" label="评价分">{{ form.commentScore }}</a-descriptions-item>
            <a-descriptions-item v-if="hasText(form.invitationCode)" label="邀请码">{{ form.invitationCode }}</a-descriptions-item>
            <a-descriptions-item label="注册时间">{{ parseTime(form.createTime) || '-' }}</a-descriptions-item>
          </a-descriptions>
        </div>

        <div class="detail-section">
          <a-divider orientation="left" class="detail-divider">实名认证信息</a-divider>
          <template v-if="showRealNameAuthSection">
          <a-descriptions :column="4" bordered size="small">
            <a-descriptions-item label="真实姓名">{{ authenticationInfo.name || '-' }}</a-descriptions-item>
            <a-descriptions-item label="身份证号">{{ displayIdentification }}</a-descriptions-item>
            <a-descriptions-item label="提交时间">{{ parseTime(authenticationInfo.createTime) || '-' }}</a-descriptions-item>
            <a-descriptions-item label="认证状态">
              <a-tag v-if="authenticationInfo.status != null" :color="getAuthenticationStatusColor(authenticationInfo.status)">
                {{ getAuthenticationStatusText(authenticationInfo.status) }}
              </a-tag>
              <span v-else>-</span>
            </a-descriptions-item>
            <a-descriptions-item label="身份证正面">
              <img
                v-if="authenticationInfo.identityFront"
                :src="authenticationInfo.identityFront"
                class="identity-image"
                alt="身份证正面"
                @click="previewImage(authenticationInfo.identityFront)"
              />
              <span v-else>-</span>
            </a-descriptions-item>
            <a-descriptions-item label="身份证反面">
              <img
                v-if="authenticationInfo.identityInverse"
                :src="authenticationInfo.identityInverse"
                class="identity-image"
                alt="身份证反面"
                @click="previewImage(authenticationInfo.identityInverse)"
              />
              <span v-else>-</span>
            </a-descriptions-item>
          </a-descriptions>
          </template>
          <a-empty v-else description="暂无实名认证信息" :image="simpleEmpty" />
        </div>
        <div v-if="form.type === 1" class="detail-section">
          <a-divider orientation="left" class="detail-divider">师傅账户信息</a-divider>
          <template v-if="showMasterWalletSection">
          <a-descriptions :column="3" bordered size="small">
            <a-descriptions-item label="总金额">
              <span>{{ formatMoney(masterWalletInfo.allMoney) }}</span>
              <a-button type="link" size="small" class="wallet-record-link-btn" @click="openWalletRecordListModal">
                查看
              </a-button>
            </a-descriptions-item>
            <a-descriptions-item label="冻结金额">{{ formatMoney(masterWalletInfo.freezeMoney) }}</a-descriptions-item>
            <a-descriptions-item label="可提现金额">{{ formatMoney(masterWalletInfo.withdrawableMoney) }}</a-descriptions-item>
          </a-descriptions>
          </template>
          <a-empty v-else description="暂无账户信息" :image="simpleEmpty" />
        </div>
        <div v-if="form.type === 0" class="detail-section">
          <a-divider orientation="left" class="detail-divider">订单列表</a-divider>
          <template v-if="orderLoading || orderTotal > 0">
          <div class="detail-table-wrap">
            <a-table
              rowKey="id"
              :loading="orderLoading"
              :data-source="orderList"
              :columns="orderColumns"
              :pagination="false"
              size="small"
              bordered
              :scroll="{ x: 1500 }"
            >
              <span slot="type" slot-scope="text, record">
                <a-tag v-if="record.type === 0" color="blue">找个人</a-tag>
                <a-tag v-else-if="record.type === 1" color="green">找公司</a-tag>
                <span v-else>-</span>
              </span>
              <span slot="status" slot-scope="text, record">
                <a-tag :color="getOrderStatusColor(record.status)">{{ getOrderStatusText(record.status) }}</a-tag>
              </span>
              <span slot="number" slot-scope="text, record">
                {{ getOrderWorkerCount(record) }}
              </span>
              <span slot="pricingMethod" slot-scope="text, record">
                <a-tag v-if="record.pricingMethod === 0">议价</a-tag>
                <a-tag v-else-if="record.pricingMethod === 1" color="blue">按时计价</a-tag>
                <a-tag v-else-if="record.pricingMethod === 2" color="green">按天计价</a-tag>
                <a-tag v-else-if="record.pricingMethod === 3" color="purple">按量计价</a-tag>
                <a-tag v-else-if="record.pricingMethod === 4" color="orange">一口价</a-tag>
                <span v-else>-</span>
              </span>
              <span slot="money" slot-scope="text, record">
                {{ formatMoney(record.money) }}
              </span>
              <span slot="payAmount" slot-scope="text, record">
                {{ formatMoney(record.payAmount) }}
              </span>
              <span slot="createTime" slot-scope="text, record">
                {{ parseTime(record.createTime) || '-' }}
              </span>
              <span slot="action" slot-scope="text, record">
                <a @click="handleOrderDetail(record)">查看</a>
              </span>
            </a-table>
            <a-pagination
              class="ant-table-pagination"
              size="small"
              show-size-changer
              :current="orderQuery.pageNum"
              :total="orderTotal"
              :page-size="orderQuery.pageSize"
              :showTotal="total => `共 ${total} 条`"
              @showSizeChange="onOrderPageSizeChange"
              @change="onOrderPageChange"
            />
          </div>
          </template>
          <a-empty v-else description="暂无订单记录" :image="simpleEmpty" />
        </div>

        <div v-if="form.type === 1" class="detail-section">
          <a-divider orientation="left" class="detail-divider">工种认证信息</a-divider>
          <template v-if="professionAuthenticationList.length > 0">
          <div class="detail-table-wrap">
            <a-table
              rowKey="id"
              :data-source="professionAuthenticationList"
              :pagination="false"
              size="small"
              bordered
            >
              <a-table-column title="工种名称" data-index="professionName" align="center" />
              <a-table-column title="到期时间" data-index="expirationDate" align="center">
                <template slot-scope="text, record">
                  {{ record.expirationDate || '-' }}
                </template>
              </a-table-column>
              <a-table-column title="审核状态" data-index="examineStatus" align="center">
                <template slot-scope="text, record">
                  <a-tag :color="getProfessionStatusColor(record.examineStatus)">
                    {{ getProfessionStatusText(record.examineStatus) }}
                  </a-tag>
                </template>
              </a-table-column>
              <a-table-column title="审核原因" data-index="examineReason" align="center">
                <template slot-scope="text, record">
                  {{ record.examineReason || '-' }}
                </template>
              </a-table-column>
              <a-table-column title="提交时间" data-index="createTime" align="center">
                <template slot-scope="text, record">
                  {{ parseTime(record.createTime) || '-' }}
                </template>
              </a-table-column>
            </a-table>
          </div>
          </template>
          <a-empty v-else description="暂无工种认证信息" :image="simpleEmpty" />
        </div>

        <div v-if="form.type === 1" class="detail-section">
          <a-divider orientation="left" class="detail-divider">接过的订单</a-divider>
          <template v-if="masterOrderLoading || masterOrderTotal > 0">
          <div class="detail-table-wrap">
            <a-table
              rowKey="id"
              :loading="masterOrderLoading"
              :data-source="masterOrderList"
              :columns="masterOrderColumns"
              :pagination="false"
              size="small"
              bordered
              :scroll="{ x: 1200 }"
            >
              <span slot="masterOrderStatus" slot-scope="text, record">
                <a-tag :color="getMasterOrderStatusColor(record.masterOrderStatus)">
                  {{ getMasterOrderStatusText(record.masterOrderStatus) }}
                </a-tag>
              </span>
              <span slot="aftermarketStatus" slot-scope="text, record">
                <a-tag :color="getMasterAftermarketStatusColor(record.aftermarketStatus)">
                  {{ getMasterAftermarketStatusText(record.aftermarketStatus) }}
                </a-tag>
              </span>
              <span slot="orderUnitPrice" slot-scope="text, record">
                {{ formatMoney(record.orderUnitPrice) }}
              </span>
              <span slot="orderTotalPrice" slot-scope="text, record">
                {{ formatMoney(record.orderTotalPrice) }}
              </span>
              <span slot="bondPrice" slot-scope="text, record">
                {{ formatMoney(record.bondPrice) }}
              </span>
              <span slot="createTime" slot-scope="text, record">
                {{ parseTime(record.createTime) || '-' }}
              </span>
              <span slot="action" slot-scope="text, record">
                <a @click="handleMasterOrderDetail(record)">查看</a>
              </span>
            </a-table>
            <a-pagination
              class="ant-table-pagination"
              size="small"
              show-size-changer
              :current="masterOrderQuery.pageNum"
              :total="masterOrderTotal"
              :page-size="masterOrderQuery.pageSize"
              :showTotal="total => `共 ${total} 条`"
              @showSizeChange="onMasterOrderPageSizeChange"
              @change="onMasterOrderPageChange"
            />
          </div>
          </template>
          <a-empty v-else description="暂无订单记录" :image="simpleEmpty" />
        </div>
      </div>
    </a-spin>
    <order-detail-drawer ref="orderDetailDrawer" @ok="loadOrderList" />
    <master-order-detail-drawer ref="masterOrderDetailDrawer" @ok="loadMasterOrderList" />
    <a-modal
      title="账户流水"
      :visible="walletRecordListVisible"
      width="900px"
      wrap-class-name="wallet-record-list-modal"
      :footer="null"
      destroy-on-close
      @cancel="closeWalletRecordListModal"
    >
      <div class="wallet-record-modal-content">
        <div class="detail-table-wrap wallet-record-list-wrap wallet-record-table-wrap">
          <a-table
            rowKey="id"
            :loading="walletRecordLoading"
            :data-source="walletRecordList"
            :columns="walletRecordColumns"
            :pagination="false"
            :scroll="walletRecordTableScroll"
            size="small"
            bordered
          >
            <span slot="recordType" slot-scope="text, record">
              <a-tag :color="record.type === 0 ? 'green' : 'volcano'">{{ record.type === 0 ? '收入' : record.type === 1 ? '支出' : '-' }}</a-tag>
            </span>
            <span slot="recordMoney" slot-scope="text, record">
              {{ formatMoney(record.money) }}
            </span>
            <span slot="recordCreateTime" slot-scope="text, record">
              {{ parseTime(record.createTime) || '-' }}
            </span>
          </a-table>
        </div>
        <div class="wallet-record-list-footer">
          <a-pagination
            class="ant-table-pagination wallet-record-pagination"
            size="small"
            show-size-changer
            :current="walletRecordQuery.pageNum"
            :total="walletRecordTotal"
            :page-size="walletRecordQuery.pageSize"
            :showTotal="total => `共 ${total} 条`"
            @showSizeChange="onWalletRecordPageSizeChange"
            @change="onWalletRecordPageChange"
          />
        </div>
      </div>
    </a-modal>
    <a-modal
      title="流水详情"
      :visible="walletRecordDetailVisible"
      :footer="null"
      width="520px"
      destroy-on-close
      @cancel="walletRecordDetailVisible = false"
    >
      <a-spin :spinning="walletRecordDetailLoading">
        <a-descriptions v-if="walletRecordDetail" :column="1" bordered size="small">
          <a-descriptions-item label="记录ID">{{ walletRecordDetail.id }}</a-descriptions-item>
          <a-descriptions-item label="师傅用户ID">{{ walletRecordDetail.userId }}</a-descriptions-item>
          <a-descriptions-item label="类型">
            <a-tag :color="walletRecordDetail.type === 0 ? 'green' : 'volcano'">
              {{ walletRecordDetail.type === 0 ? '收入' : walletRecordDetail.type === 1 ? '支出' : '-' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="金额">{{ formatMoney(walletRecordDetail.money) }}</a-descriptions-item>
          <a-descriptions-item label="事项">{{ walletRecordDetail.title || '-' }}</a-descriptions-item>
          <a-descriptions-item label="备注">{{ walletRecordDetail.remark || '-' }}</a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ parseTime(walletRecordDetail.createTime) || '-' }}</a-descriptions-item>
        </a-descriptions>
      </a-spin>
    </a-modal>
  </a-drawer>
</template>

<script>
import { Empty } from 'ant-design-vue'
import { getUser } from '@/api/biz/user'
import { pageOrder } from '@/api/order/order'
import { pageOrder as pageMasterOrder } from '@/api/master/order'
import { listWallet } from '@/api/master/wallet'
import { pageWalletRecord, getWalletRecord } from '@/api/master/walletRecord'
import OrderDetailDrawer from '@/views/order/order/modules/CreateForm'
import MasterOrderDetailDrawer from '@/views/master/order/modules/CreateForm'

export default {
  name: 'DetailForm',
  components: {
    OrderDetailDrawer,
    MasterOrderDetailDrawer
  },
  data () {
    return {
      open: false,
      loading: false,
      orderLoading: false,
      masterOrderLoading: false,
      formTitle: '用户详情',
      form: this.getDefaultForm(),
      orderList: [],
      orderTotal: 0,
      masterOrderList: [],
      masterOrderTotal: 0,
      masterWalletInfo: {
        allMoney: null,
        freezeMoney: null,
        withdrawableMoney: null
      },
      orderQuery: {
        userId: undefined,
        pageNum: 1,
        pageSize: 5
      },
      masterOrderQuery: {
        userId: undefined,
        pageNum: 1,
        pageSize: 5
      },
      walletRecordLoading: false,
      walletRecordList: [],
      walletRecordTotal: 0,
      walletRecordQuery: {
        userId: undefined,
        pageNum: 1,
        pageSize: 5
      },
      walletRecordListVisible: false,
      walletRecordDetailVisible: false,
      walletRecordDetail: null,
      walletRecordDetailLoading: false,
      walletRecordColumns: [
        {
          title: '类型',
          dataIndex: 'type',
          width: 90,
          scopedSlots: { customRender: 'recordType' },
          align: 'center'
        },
        {
          title: '金额',
          dataIndex: 'money',
          width: 110,
          scopedSlots: { customRender: 'recordMoney' },
          align: 'center'
        },
        {
          title: '事项',
          dataIndex: 'title',
          ellipsis: true,
          align: 'center'
        },
        {
          title: '创建时间',
          dataIndex: 'createTime',
          width: 170,
          scopedSlots: { customRender: 'recordCreateTime' },
          align: 'center'
        }
      ],
      orderColumns: [
        {
          title: '招工标题',
          dataIndex: 'title',
          width: 180,
          ellipsis: true,
          align: 'center'
        },
        {
          title: '个人/企业',
          dataIndex: 'type',
          width: 100,
          scopedSlots: { customRender: 'type' },
          align: 'center'
        },
        {
          title: '订单状态',
          dataIndex: 'status',
          width: 100,
          scopedSlots: { customRender: 'status' },
          align: 'center'
        },
        {
          title: '师傅人数',
          dataIndex: 'numberOfWorkers',
          width: 100,
          scopedSlots: { customRender: 'number' },
          align: 'center'
        },
        {
          title: '计价方式',
          dataIndex: 'pricingMethod',
          width: 100,
          scopedSlots: { customRender: 'pricingMethod' },
          align: 'center'
        },
        {
          title: '金额',
          dataIndex: 'money',
          width: 100,
          scopedSlots: { customRender: 'money' },
          align: 'center'
        },
        {
          title: '支付金额',
          dataIndex: 'payAmount',
          width: 110,
          scopedSlots: { customRender: 'payAmount' },
          align: 'center'
        },
        {
          title: '创建时间',
          dataIndex: 'createTime',
          width: 170,
          scopedSlots: { customRender: 'createTime' },
          align: 'center'
        },
        {
          title: '操作',
          dataIndex: 'action',
          fixed: 'right',
          width: 80,
          scopedSlots: { customRender: 'action' },
          align: 'center'
        }
      ],
      masterOrderColumns: [
        {
          title: '订单编号',
          dataIndex: 'orderNo',
          width: 180,
          ellipsis: true,
          align: 'center'
        },
        {
          title: '标题',
          dataIndex: 'title',
          width: 180,
          ellipsis: true,
          align: 'center'
        },
        {
          title: '订单状态',
          dataIndex: 'masterOrderStatus',
          width: 100,
          scopedSlots: { customRender: 'masterOrderStatus' },
          align: 'center'
        },
        {
          title: '售后状态',
          dataIndex: 'aftermarketStatus',
          width: 110,
          scopedSlots: { customRender: 'aftermarketStatus' },
          align: 'center'
        },
        {
          title: '售后次数',
          dataIndex: 'aftermarketNum',
          width: 90,
          align: 'center'
        },
        {
          title: '订单单价',
          dataIndex: 'orderUnitPrice',
          width: 100,
          scopedSlots: { customRender: 'orderUnitPrice' },
          align: 'center'
        },
        {
          title: '订单总价',
          dataIndex: 'orderTotalPrice',
          width: 100,
          scopedSlots: { customRender: 'orderTotalPrice' },
          align: 'center'
        },
        {
          title: '保证金金额',
          dataIndex: 'bondPrice',
          width: 110,
          scopedSlots: { customRender: 'bondPrice' },
          align: 'center'
        },
        {
          title: '接单时间',
          dataIndex: 'createTime',
          width: 170,
          scopedSlots: { customRender: 'createTime' },
          align: 'center'
        },
        {
          title: '操作',
          dataIndex: 'action',
          width: 80,
          fixed: 'right',
          scopedSlots: { customRender: 'action' },
          align: 'center'
        }
      ]
    }
  },
  computed: {
    authenticationInfo () {
      return this.form.authentication || {}
    },
    displayIdentification () {
      return this.authenticationInfo.identification || '-'
    },
    simpleEmpty () {
      return Empty.PRESENTED_IMAGE_SIMPLE
    },
    professionAuthenticationList () {
      return this.form.professionAuthenticationList || []
    },
    walletRecordTableScroll () {
      const h = typeof window !== 'undefined' ? window.innerHeight : 800
      const y = Math.max(240, Math.min(520, h - 300))
      return { x: 820, y }
    },
    /** 存在任一实名相关字段则展示「实名认证信息」整块 */
    showRealNameAuthSection () {
      const a = this.form.authentication
      if (!a || typeof a !== 'object') {
        return false
      }
      const t = s => s != null && String(s).trim() !== ''
      return !!(a.id || t(a.name) || t(a.identification) || t(a.identityFront) || t(a.identityInverse) ||
        a.status != null || a.createTime)
    },
    /** 后端存在钱包记录（有任一金额字段）才展示师傅账户 */
    showMasterWalletSection () {
      if (this.form.type !== 1) {
        return false
      }
      const w = this.masterWalletInfo
      return w.allMoney != null || w.freezeMoney != null || w.withdrawableMoney != null
    },
    /** 雇主有订单记录才展示订单列表 */
    showEmployerOrderSection () {
      if (this.form.type !== 0) {
        return false
      }
      return this.orderLoading || this.orderTotal > 0
    },
    /** 有工种认证记录才展示 */
    showProfessionAuthSection () {
      return this.professionAuthenticationList.length > 0
    },
    /** 师傅有接单记录才展示 */
    showMasterOrderSection () {
      if (this.form.type !== 1) {
        return false
      }
      return this.masterOrderLoading || this.masterOrderTotal > 0
    }
  },
  methods: {
    hasText (value) {
      return value != null && String(value).trim() !== ''
    },
    getDefaultForm () {
      return {
        id: null,
        type: null,
        mobile: null,
        nickname: null,
        contactPersonName: null,
        contactPersonPhone: null,
        creditScore: null,
        commentScore: null,
        invitationCode: null,
        createTime: null,
        authentication: null,
        professionAuthenticationList: []
      }
    },
    onClose () {
      this.open = false
      this.reset()
    },
    reset () {
      this.form = this.getDefaultForm()
      this.orderList = []
      this.orderTotal = 0
      this.masterOrderList = []
      this.masterOrderTotal = 0
      this.masterWalletInfo = {
        allMoney: null,
        freezeMoney: null,
        withdrawableMoney: null
      }
      this.orderQuery = {
        userId: undefined,
        pageNum: 1,
        pageSize: 5
      }
      this.masterOrderQuery = {
        userId: undefined,
        pageNum: 1,
        pageSize: 5
      }
      this.walletRecordList = []
      this.walletRecordTotal = 0
      this.walletRecordQuery = {
        userId: undefined,
        pageNum: 1,
        pageSize: 5
      }
      this.walletRecordListVisible = false
      this.walletRecordDetailVisible = false
      this.walletRecordDetail = null
      this.walletRecordDetailLoading = false
    },
    handleDetail (row, ids) {
      const id = row ? row.id : ids
      if (!id) {
        this.$message.warning('请选择一条记录')
        return
      }
      this.loading = true
      getUser({ id }).then(response => {
        this.form = {
          ...this.getDefaultForm(),
          ...(response.data || {})
        }
        this.open = true
        this.formTitle = '用户详情'
        if (this.form.type === 0) {
          this.orderQuery.userId = this.form.id
          this.loadOrderList()
        } else if (this.form.type === 1) {
          this.masterOrderQuery.userId = this.form.id
          this.loadMasterOrderList()
          this.loadMasterWalletInfo()
        }
      }).finally(() => {
        this.loading = false
      })
    },
    loadOrderList () {
      if (this.form.type !== 0 || !this.orderQuery.userId) {
        return
      }
      this.orderLoading = true
      pageOrder(this.orderQuery).then(response => {
        this.orderList = response.data.records || []
        this.orderTotal = response.data.total || 0
      }).finally(() => {
        this.orderLoading = false
      })
    },
    onOrderPageSizeChange (current, pageSize) {
      this.orderQuery.pageNum = 1
      this.orderQuery.pageSize = pageSize
      this.loadOrderList()
    },
    onOrderPageChange (current, pageSize) {
      this.orderQuery.pageNum = current
      this.orderQuery.pageSize = pageSize
      this.loadOrderList()
    },
    loadMasterOrderList () {
      if (this.form.type !== 1 || !this.masterOrderQuery.userId) {
        return
      }
      this.masterOrderLoading = true
      pageMasterOrder(this.masterOrderQuery).then(response => {
        this.masterOrderList = response.data.records || []
        this.masterOrderTotal = response.data.total || 0
      }).finally(() => {
        this.masterOrderLoading = false
      })
    },
    loadMasterWalletInfo () {
      if (this.form.type !== 1 || !this.form.id) {
        return
      }
      listWallet({ userId: this.form.id }).then(response => {
        const wallet = (response.data && response.data[0]) || {}
        this.masterWalletInfo = {
          allMoney: wallet.allMoney != null ? wallet.allMoney : null,
          freezeMoney: wallet.freezeMoney != null ? wallet.freezeMoney : null,
          withdrawableMoney: wallet.withdrawableMoney != null ? wallet.withdrawableMoney : null
        }
      })
    },
    loadWalletRecordList () {
      if (this.form.type !== 1 || !this.walletRecordQuery.userId) {
        return
      }
      this.walletRecordLoading = true
      pageWalletRecord(this.walletRecordQuery).then(response => {
        this.walletRecordList = response.data.records || []
        this.walletRecordTotal = response.data.total || 0
      }).finally(() => {
        this.walletRecordLoading = false
      })
    },
    onWalletRecordPageSizeChange (current, pageSize) {
      this.walletRecordQuery.pageNum = 1
      this.walletRecordQuery.pageSize = pageSize
      this.loadWalletRecordList()
    },
    onWalletRecordPageChange (current, pageSize) {
      this.walletRecordQuery.pageNum = current
      this.walletRecordQuery.pageSize = pageSize
      this.loadWalletRecordList()
    },
    openWalletRecordDetail (record) {
      if (!record || record.id == null) {
        return
      }
      this.walletRecordDetailVisible = true
      this.walletRecordDetail = null
      this.walletRecordDetailLoading = true
      getWalletRecord({ id: record.id }).then(response => {
        this.walletRecordDetail = response.data || {}
      }).finally(() => {
        this.walletRecordDetailLoading = false
      })
    },
    onMasterOrderPageSizeChange (current, pageSize) {
      this.masterOrderQuery.pageNum = 1
      this.masterOrderQuery.pageSize = pageSize
      this.loadMasterOrderList()
    },
    onMasterOrderPageChange (current, pageSize) {
      this.masterOrderQuery.pageNum = current
      this.masterOrderQuery.pageSize = pageSize
      this.loadMasterOrderList()
    },
    handleOrderDetail (record) {
      this.$refs.orderDetailDrawer.handleUpdate(record, undefined)
    },
    handleMasterOrderDetail (record) {
      this.$refs.masterOrderDetailDrawer.handleUpdate(record, undefined)
    },
    getOrderWorkerCount (record) {
      const numberOfWorkers = Number(record.numberOfWorkers || 0)
      const man = Number(record.man || 0)
      const woman = Number(record.woman || 0)
      return numberOfWorkers + man + woman
    },
    formatMoney (value) {
      return value != null ? `¥${Number(value).toFixed(2)}` : '-'
    },
    getAuthenticationStatusText (status) {
      const n = Number(status)
      if (Number.isNaN(n)) {
        return '-'
      }
      const map = { 0: '待审核', 1: '已审核', [-1]: '审核驳回' }
      return map[n] != null ? map[n] : '-'
    },
    getAuthenticationStatusColor (status) {
      const n = Number(status)
      if (Number.isNaN(n)) {
        return 'default'
      }
      const map = { 0: 'orange', 1: 'green', [-1]: 'red' }
      return map[n] != null ? map[n] : 'default'
    },
    getProfessionStatusText (status) {
      const statusMap = {
        0: '待审核',
        1: '已通过',
        2: '已拒绝'
      }
      return statusMap[status] || '-'
    },
    getProfessionStatusColor (status) {
      const colorMap = {
        0: 'orange',
        1: 'green',
        2: 'red'
      }
      return colorMap[status] || 'default'
    },
    getOrderStatusText (status) {
      const statusMap = {
        '-2': '未支付',
        '-1': '未实名认证',
        0: '接单中',
        1: '服务中',
        2: '已完成',
        3: '售后中',
        4: '已取消'
      }
      return statusMap[status] || '-'
    },
    getOrderStatusColor (status) {
      const colorMap = {
        '-2': 'orange',
        '-1': 'gold',
        0: 'blue',
        1: 'cyan',
        2: 'green',
        3: 'purple',
        4: 'red'
      }
      return colorMap[status] || 'default'
    },
    getMasterOrderStatusText (status) {
      const statusMap = {
        '-1': '待支付',
        0: '接单中',
        1: '待服务',
        2: '服务中',
        3: '待评价',
        4: '已完成',
        5: '售后中',
        6: '售后完成',
        7: '已关闭'
      }
      return statusMap[status] || '-'
    },
    getMasterOrderStatusColor (status) {
      const colorMap = {
        '-1': 'gold',
        0: 'blue',
        1: 'orange',
        2: 'cyan',
        3: 'purple',
        4: 'green',
        5: 'gold',
        6: 'green',
        7: 'red'
      }
      return colorMap[status] || 'default'
    },
    getMasterAftermarketStatusText (status) {
      const statusMap = {
        0: '无需售后',
        1: '待联系客户',
        2: '待售后完成',
        3: '已提交售后',
        4: '售后完成',
        5: '平台审核'
      }
      return statusMap[status] || '-'
    },
    getMasterAftermarketStatusColor (status) {
      const colorMap = {
        0: 'blue',
        1: 'orange',
        2: 'orange',
        3: 'blue',
        4: 'green',
        5: 'purple'
      }
      return colorMap[status] || 'default'
    },
    openWalletRecordListModal () {
      if (this.form.type !== 1 || !this.form.id) {
        return
      }
      this.walletRecordListVisible = true
      this.walletRecordQuery.userId = this.form.id
      this.walletRecordQuery.pageNum = 1
      this.loadWalletRecordList()
    },
    closeWalletRecordListModal () {
      this.walletRecordListVisible = false
    },
    previewImage (url) {
      if (!url) {
        return
      }
      window.open(url, '_blank')
    }
  }
}
</script>

<style scoped>
.user-detail-wrap {
  padding-bottom: 8px;
}

.user-detail-wrap .detail-section + .detail-section {
  margin-top: 20px;
}

.user-detail-wrap .detail-divider {
  margin: 8px 0 16px;
}

.user-detail-wrap .identity-image {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #f0f0f0;
}

.user-detail-wrap .ant-table-pagination {
  margin-top: 16px;
}

.user-detail-wrap .detail-table-wrap {
  padding: 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
}

.wallet-record-link-btn {
  padding-left: 8px;
  height: auto;
}

.wallet-record-list-wrap {
  margin: 0;
}

.wallet-record-list-footer {
  flex-shrink: 0;
  padding-top: 12px;
  margin-top: 4px;
}

.wallet-record-pagination {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  text-align: right;
}
</style>

<style lang="less">
/* Modal 挂载在 body：限制高度 + flex，表格区内滚动，分页固定在弹窗底部可见区域 */
.wallet-record-list-modal .ant-modal {
  top: 40px;
  padding-bottom: 24px;
}

.wallet-record-list-modal .ant-modal-body {
  max-height: calc(100vh - 120px);
  padding-top: 12px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.wallet-record-list-modal .wallet-record-modal-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  max-height: calc(100vh - 180px);
  overflow: hidden;
}

.wallet-record-list-modal .wallet-record-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
